import React, { useState } from 'react';
import { MoreVertical, Edit2, CheckCircle, XCircle, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import type { TrainingPlan } from '../types';
import { parseLocalDate } from '../utils/dateUtils';

interface TrainingPlanBannerProps {
  plan: TrainingPlan;
  weekNumber: number;
  isExpired: boolean;
  dayProgress: Array<{ dayNumber: number; label?: string; completed: boolean }>;
  planHistory: Array<{ id: string; date: string; routineName: string; exerciseCount: number }>;
  onEdit: () => void;
  onComplete: () => void;
  onAbandon: () => void;
}

export function TrainingPlanBanner({
  plan,
  weekNumber,
  isExpired,
  dayProgress,
  planHistory,
  onEdit,
  onComplete,
  onAbandon,
}: TrainingPlanBannerProps) {
  const { darkMode, theme } = useTheme();
  const [showMenu, setShowMenu] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const progress = Math.min((weekNumber / plan.duration_weeks) * 100, 100);
  const completedDays = dayProgress.filter((day) => day.completed).length;
  const heroSurface = darkMode
    ? 'border-[#5a3925] bg-[radial-gradient(circle_at_top_left,_rgba(217,119,69,0.18),_transparent_42%),linear-gradient(135deg,_rgba(36,28,22,0.95),_rgba(23,20,17,0.98))]'
    : 'border-[#e3c4af] bg-[radial-gradient(circle_at_top_left,_rgba(180,83,42,0.14),_transparent_40%),linear-gradient(135deg,_rgba(255,249,243,0.95),_rgba(247,238,228,0.98))]';
  const metricSurface = darkMode
    ? 'border-stone-800 bg-[#1e1914]'
    : 'border-white/80 bg-white/70';

  return (
    <div className={`animate-rise-in lift-card relative overflow-hidden rounded-[28px] border p-5 sm:p-6 ${heroSurface}`}>
      <div className='mb-4 flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <p className={`text-[11px] uppercase tracking-[0.26em] ${theme.textSecondary}`}>
            Plan activo
          </p>
          <h3 className='mt-2 text-2xl sm:text-3xl font-black tracking-[-0.04em] truncate pr-2'>
            {plan.name}
          </h3>
          <p className={`mt-2 text-sm ${theme.textSecondary}`}>
            Semana {weekNumber}/{plan.duration_weeks} · {completedDays}/{dayProgress.length || plan.days.length} días
          </p>
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setShowMenu(!showMenu)}
            className={`pressable rounded-2xl border p-2 ${metricSurface}`}
            aria-label="Opciones del plan"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
              <div
                className={`absolute right-0 top-full mt-2 z-50 w-56 rounded-2xl border ${metricSurface} shadow-xl py-1.5`}
              >
                <button
                  onClick={() => { setShowMenu(false); setShowDetails((prev) => !prev); }}
                  className={`pressable w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left ${theme.hover}`}
                >
                  <MoreVertical className="w-4 h-4" />
                  {showDetails ? 'Ocultar detalle' : 'Semana e historial'}
                </button>
                <button
                  onClick={() => { setShowMenu(false); onEdit(); }}
                  className={`pressable w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left ${theme.hover}`}
                >
                  <Edit2 className="w-4 h-4" />
                  Editar plan
                </button>
                <button
                  onClick={() => { setShowMenu(false); onComplete(); }}
                  className={`pressable w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left ${theme.hover} text-green-500`}
                >
                  <CheckCircle className="w-4 h-4" />
                  Completar plan
                </button>
                <button
                  onClick={() => { setShowMenu(false); onAbandon(); }}
                  className={`pressable w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left ${theme.hover} text-red-500`}
                >
                  <XCircle className="w-4 h-4" />
                  Abandonar plan
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div
        role='progressbar'
        aria-label='Semanas del plan'
        aria-valuemin={0}
        aria-valuemax={plan.duration_weeks}
        aria-valuenow={Math.min(weekNumber, plan.duration_weeks)}
        className={`w-full h-2.5 rounded-full ${darkMode ? 'bg-stone-800' : 'bg-stone-200'}`}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#b4532a] to-[#d97745] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {showDetails && (
        <div className={`mt-4 space-y-4 rounded-[24px] border p-4 ${metricSurface}`}>
          <div className="flex items-center justify-between">
            <p className={`text-xs font-semibold uppercase tracking-[0.22em] ${theme.textSecondary}`}>
              Esta semana
            </p>
            <button
              type="button"
              onClick={() => setShowDetails(false)}
              className={`pressable p-1.5 rounded-xl ${theme.hover}`}
              aria-label="Cerrar detalle del plan"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {dayProgress.length > 0 && (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                {dayProgress.map((day) => (
                  <div
                    key={`plan-day-${day.dayNumber}`}
                    className={`w-10 h-10 rounded-2xl border text-[12px] font-semibold flex items-center justify-center transition-colors ${
                      day.completed
                        ? 'bg-emerald-600 border-emerald-500 text-white'
                        : `${metricSurface} ${theme.textSecondary}`
                    }`}
                    title={`Día ${day.dayNumber}${day.label ? ` — ${day.label}` : ''}`}
                  >
                    {day.dayNumber}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`rounded-[20px] border p-3 ${darkMode ? 'border-stone-800 bg-[#17130f]' : 'border-stone-200 bg-stone-50/85'}`}>
            <p className={`text-xs font-semibold uppercase tracking-[0.22em] mb-3 ${theme.textSecondary}`}>
              Historial
            </p>
            {planHistory.length === 0 ? (
              <p className={`text-xs ${theme.textSecondary}`}>
                Sin entrenamientos.
              </p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                {planHistory.slice(0, 8).map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate font-medium">{entry.routineName}</span>
                    <span className={`${theme.textSecondary} shrink-0`}>
                      {parseLocalDate(entry.date).toLocaleDateString('es-ES')} · {entry.exerciseCount} ej.
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isExpired && (
        <p className="text-xs text-amber-500 mt-3">
          Plan terminado. Completalo o extendelo desde el menú.
        </p>
      )}
    </div>
  );
}
